import type { ExternalPet } from "./external-catalog";
import type { Pet, PetSpecies } from "../data/pets";

export interface MirroredPetRow {
  id: string;
  source_provider: string;
  source_id: string;
  source_url: string;
  display_name: string;
  description: string;
  kind: string;
  creator_name: string;
  tags_json: string;
  r2_zip_key: string;
  r2_manifest_key: string;
  r2_spritesheet_key: string;
  r2_poster_key: string;
  source_hash: string;
  asset_license: string;
  mirror_permission: string;
  view_count: number;
  like_count: number;
  download_count: number;
  status: string;
  mirrored_at?: string;
}

function tags(row: MirroredPetRow): string[] {
  try { return JSON.parse(row.tags_json) as string[] } catch { return [] }
}

export function mirroredRowToExternalPet(row: MirroredPetRow): ExternalPet {
  return {
    id: row.id,
    name: row.display_name,
    description: row.description,
    kind: row.kind,
    creator: row.creator_name,
    tags: tags(row),
    posterUrl: `/api/pets/${row.id}/poster`,
    previewUrl: `/api/pets/${row.id}/spritesheet`,
    downloadUrl: `/api/pets/${row.id}/download`,
    detailUrl: `/pets/${row.id}`,
    sourceUrl: row.source_url,
    installCommand: `npx codexpetlab add ${row.id}`,
    source: row.source_provider === "codex-pet.com" ? "" : row.source_provider,
    views: row.view_count,
    likes: row.like_count,
    downloads: row.download_count,
  };
}

export function mirroredRowToShareData(row: MirroredPetRow) {
  return {
    id: row.id,
    displayName: row.display_name,
    description: row.description,
    spritesheetPath: "spritesheet.webp",
    spriteVersionNumber: 2,
    kind: row.kind,
    ownerHandle: row.creator_name,
    ownerName: row.creator_name,
    viewCount: row.view_count,
    downloadCount: row.download_count,
    likeCount: row.like_count,
    tags: tags(row),
    spritesheetUrl: `/api/pets/${row.id}/spritesheet`,
    posterUrl: `/api/pets/${row.id}/poster`,
    downloadUrl: `/api/pets/${row.id}/download`,
    sourceProvider: row.source_provider === "codex-pet.com" ? "" : row.source_provider,
    sourceUrl: row.source_url,
    assetLicense: row.asset_license,
    validationReport: {
      manifestId: row.id,
      atlasSize: "1536x2288",
      cellSize: "192x208",
      statesDetected: 11,
      spriteVersionNumber: 2,
    },
  };
}

export type MirroredShareData = ReturnType<typeof mirroredRowToShareData>;

export function mirroredShareDataToPet(data: MirroredShareData): Pet {
  const species: PetSpecies = data.kind === "animal" ? "cat" : data.kind === "object" ? "bot" : data.kind === "person" ? "bot" : "fox";
  return {
    id: data.id,
    name: data.displayName,
    creator: data.ownerName,
    creatorHandle: data.ownerHandle,
    description: data.description,
    species,
    tags: data.tags,
    collection: "Workshop",
    createdAt: new Date().toISOString().slice(0, 10),
    views: data.viewCount,
    likes: data.likeCount,
    downloads: data.downloadCount,
    palette: { background: "#151d2d", primary: "#4d8dff", secondary: "#8b5cf6", accent: "#43d18b", ink: "#f2f6ff" },
  };
}
